import React from "react";
import { StatusCodes } from "http-status-codes";
import { v4 as uuidv4 } from "uuid";
import { OrIcon } from "../../shared_modules/ui/components";
import type {
  HeaderBusinessMenuItem,
  OrCardFeatureProps,
  OrCardShortcutProps,
  OrCardSummaryProps,
  SidebarBusinessItem,
} from "../../shared_modules/ui/components";
import { useProfileRepository } from "../../profile/useProfileRepository";
import { showOrToast } from "../../shared_modules/ui/components/atoms/or-toast/or-toast";
import { useRegistrationVerification } from "../registration-verification-context";

interface CompanyPreRegisterPayload {
  captcha: string;
  companyName: string;
  id: string;
  name: string;
  password: string;
  username: string;
}

interface CompanyPreRegisterResponse {
  status: number;
}

interface CompanyProfileRepository {
  createProfile: (body: CompanyPreRegisterPayload) => Promise<CompanyPreRegisterResponse>;
}

interface MarketingPreRegisterValues {
  captcha: string;
  email: string;
  password: string;
  policy: boolean;
  showPassword: boolean;
}

interface MarketingPreRegisterErrors {
  captcha?: string;
  email?: string;
  password?: string;
  policy?: string;
  submit?: string;
}

export interface UseMarketingPreRegisterResult {
  employeeCards: readonly (OrCardFeatureProps | OrCardShortcutProps)[];
  errors: MarketingPreRegisterErrors;
  formValues: MarketingPreRegisterValues;
  handleEmailChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handlePasswordChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handlePolicyChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleRecaptchaChange: (value: string | null) => void;
  handleSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  headerMenuItems: readonly HeaderBusinessMenuItem[];
  humanResourcesCards: readonly OrCardFeatureProps[];
  isCaptchaEnabled: boolean;
  isSubmitting: boolean;
  officeCards: readonly OrCardFeatureProps[];
  setShowPassword: (showPassword: boolean) => void;
  sidebarItems: readonly SidebarBusinessItem[];
  successEmail: string;
  summaryCard: OrCardSummaryProps;
  supportCards: readonly OrCardShortcutProps[];
}

function createSummaryCard(card: OrCardSummaryProps): OrCardSummaryProps {
  return card;
}

function createFeatureCard(card: OrCardFeatureProps): OrCardFeatureProps {
  return card;
}

function createShortcutCard(card: OrCardShortcutProps): OrCardShortcutProps {
  return card;
}

export function getCompanyNameFromEmail(email: string): string {
  const fragments = email.trim().split("@");
  const domain = fragments.length > 1 ? fragments[1].trim().toLowerCase() : "";

  if (domain) {
    return domain;
  }

  return "Empresa en configuracion";
}

function validateMarketingPreRegister(
  values: MarketingPreRegisterValues,
  isCaptchaEnabled: boolean,
): MarketingPreRegisterErrors {
  const errors: MarketingPreRegisterErrors = {};
  const email = values.email.trim();

  if (!email) {
    errors.email = "El email es requerido";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.email = "Escribe un email válido";
  }

  if (!values.password) {
    errors.password = "La contraseña es requerida";
  } else if (values.password.length < 8) {
    errors.password = "La contraseña debe tener un mínimo de 8 caracteres";
  }

  if (isCaptchaEnabled && !values.captcha) {
    errors.captcha = "El captcha es requerido";
  }

  if (!values.policy) {
    errors.policy = "Debes aceptar los términos y condiciones y la política de privacidad";
  }

  return errors;
}

function extractErrorMessage(error: unknown): string {
  if (typeof error === "object" && error !== null && "response" in error) {
    const response = error.response;

    if (typeof response === "object" && response !== null && "data" in response) {
      const data = response.data;

      if (typeof data === "object" && data !== null && "message" in data && typeof data.message === "string") {
        return data.message;
      }
    }
  }

  return "No hemos podido crear la cuenta. Inténtalo de nuevo.";
}

export function useMarketingPreRegister(): UseMarketingPreRegisterResult {
  const { setPendingVerificationEmail } = useRegistrationVerification();
  const profileRepository: CompanyProfileRepository = useProfileRepository();
  const isCaptchaEnabled = process.env.NODE_ENV === "production";
  const [formValues, setFormValues] = React.useState<MarketingPreRegisterValues>({
    captcha: "",
    email: "",
    password: "",
    policy: false,
    showPassword: false,
  });
  const [errors, setErrors] = React.useState<MarketingPreRegisterErrors>({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [successEmail, setSuccessEmail] = React.useState("");

  const handleEmailChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const nextEmail = event.currentTarget.value;

    setFormValues((currentValues) => ({ ...currentValues, email: nextEmail }));
    setErrors((currentErrors) => ({ ...currentErrors, email: undefined, submit: undefined }));
  }, []);

  const handlePasswordChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const nextPassword = event.currentTarget.value;

    setFormValues((currentValues) => ({ ...currentValues, password: nextPassword }));
    setErrors((currentErrors) => ({ ...currentErrors, password: undefined, submit: undefined }));
  }, []);

  const handlePolicyChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const nextPolicy = event.currentTarget.checked;

    setFormValues((currentValues) => ({ ...currentValues, policy: nextPolicy }));
    setErrors((currentErrors) => ({ ...currentErrors, policy: undefined, submit: undefined }));
  }, []);

  const handleRecaptchaChange = React.useCallback((value: string | null) => {
    setFormValues((currentValues) => ({ ...currentValues, captcha: value || "" }));
    setErrors((currentErrors) => ({ ...currentErrors, captcha: undefined, submit: undefined }));
  }, []);

  const setShowPassword = React.useCallback((showPassword: boolean) => {
    setFormValues((currentValues) => ({ ...currentValues, showPassword }));
  }, []);

  const handleSubmit = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const nextErrors = validateMarketingPreRegister(formValues, isCaptchaEnabled);
      setErrors(nextErrors);

      if (nextErrors.email || nextErrors.password || nextErrors.captcha || nextErrors.policy) {
        return;
      }

      const email = formValues.email.trim();
      setIsSubmitting(true);

      try {
        const response = await profileRepository.createProfile({
          captcha: isCaptchaEnabled ? formValues.captcha : "",
          companyName: getCompanyNameFromEmail(email),
          id: uuidv4(),
          name: email,
          password: formValues.password,
          username: email,
        });

        if (response.status !== StatusCodes.CREATED) {
          const submitError = "No hemos podido crear la cuenta. Inténtalo de nuevo.";
          setErrors({ submit: submitError });
          showOrToast(submitError, { type: "error" });
          return;
        }

        setPendingVerificationEmail(email);
        setSuccessEmail(email);
        setFormValues({ captcha: "", email, password: "", policy: false, showPassword: false });
        showOrToast("Te hemos enviado el correo para activar tu cuenta");
      } catch (error) {
        const submitError = extractErrorMessage(error);
        setErrors({ submit: submitError });
        showOrToast(submitError, { type: "error" });
      } finally {
        setIsSubmitting(false);
      }
    },
    [formValues, isCaptchaEnabled, profileRepository, setPendingVerificationEmail],
  );

  const pendingTaskItems = [
    {
      icon: <OrIcon name="documentUser" size={16} />,
      id: "worker-clock",
      isCompleted: true,
      label: "Fichajes",
      meta: "0 pendientes",
    },
    {
      icon: <OrIcon name="calendarAlert" size={16} />,
      id: "absence-list",
      isCompleted: true,
      label: "Ausencias",
      meta: "0 pendientes",
    },
    {
      icon: <OrIcon name="mail" size={16} />,
      id: "worker-invitations",
      isCompleted: true,
      label: "Invitaciones",
      meta: "0 pendientes",
    },
  ];

  const summaryCard = createSummaryCard({
    items: pendingTaskItems,
    itemsTitle: "Pendientes de gestionar",
    primaryLegendIcon: <OrIcon name="clockCircle" size={14} />,
    primaryLabel: "Pendientes",
    primaryValue: 0,
    secondaryLegendIcon: <OrIcon name="check" size={14} />,
    secondaryLabel: "Sin pendientes",
    secondaryValue: 100,
    summaryTitle: "Progreso de tareas",
    title: "Tareas Pendientes",
    total: 0,
    totalLabel: "Total",
    variant: "summary",
  });

  return {
    employeeCards: [
      createFeatureCard({
        badges: [
          { id: "workers", label: "Gestionar empleados", variant: "link" },
          { id: "worker-clock-settings", label: "Administrar código PIN", variant: "link" },
        ],
        description: "Gestiona empleados, accesos y control de fichaje desde una única plataforma de RR. HH.",
        icon: <OrIcon name="employees" size={20} />,
        title: "Gestión de Personal",
        variant: "feature",
      }),
      createFeatureCard({
        badges: [
          { id: "calendar", label: "Gestionar calendarios", variant: "link" },
          { id: "absences", label: "Gestionar ausencias", variant: "link" },
        ],
        description: "Controla calendarios laborales, gestiona ausencias y planifica vacaciones de forma sencilla.",
        icon: <OrIcon name="calendarCheck" size={20} />,
        title: "Calendarios y Ausencias",
        variant: "feature",
      }),
      createShortcutCard({
        description:
          "Planifica turnos de trabajo, organiza horarios laborales y asegura la cobertura operativa de tu equipo en tiempo real.",
        icon: <OrIcon name="calendarClock" size={20} />,
        title: "Gestionar Turnos",
        variant: "shortcut",
      }),
      createShortcutCard({
        description:
          "Supervisa el fichaje digital y controla el registro horario de tus empleados conforme a la normativa.",
        icon: <OrIcon name="workHistory" size={20} />,
        title: "Gestionar Fichajes",
        variant: "shortcut",
      }),
    ],
    errors,
    formValues,
    handleEmailChange,
    handlePasswordChange,
    handlePolicyChange,
    handleRecaptchaChange,
    handleSubmit,
    headerMenuItems: [
      { disabled: true, icon: <OrIcon name="user" size={16} />, id: "profile", label: "Perfil" },
      { disabled: true, icon: <OrIcon name="logout" size={16} />, id: "logout", label: "Cerrar sesión", tone: "danger" },
    ],
    humanResourcesCards: [
      createFeatureCard({
        badges: [
          { id: "headhunting", label: "Headhunting", variant: "link" },
          { id: "headhunting-candidates", label: "Gestión de candidatos", variant: "link" },
        ],
        description: "Gestiona candidatos y procesos de selección desde una solución de RR. HH. integral.",
        icon: <OrIcon name="humanResources" size={20} />,
        title: "Reclutamiento y Headhunting",
        variant: "feature",
      }),
    ],
    isCaptchaEnabled,
    isSubmitting,
    officeCards: [
      createFeatureCard({
        badges: [
          { id: "offices", label: "Oficinas", variant: "link" },
          { id: "departments", label: "Departamentos y áreas", variant: "link" },
        ],
        description: "Organiza oficinas, departamentos y estructura organizativa de tu empresa.",
        icon: <OrIcon name="office" size={20} />,
        title: "Gestión de Oficinas y Departamentos",
        variant: "feature",
      }),
    ],
    setShowPassword,
    sidebarItems: [
      {
        icon: <OrIcon name="home" size={20} />,
        id: "home",
        isActive: true,
        label: "Inicio",
      },
      {
        children: [
          { id: "offices-list", label: "Mis oficinas" },
          { id: "offices-departments", label: "Mis departamentos" },
        ],
        defaultExpanded: true,
        icon: <OrIcon name="office" size={20} />,
        id: "offices",
        label: "Oficinas",
      },
      {
        children: [
          { id: "workers", label: "Gestionar empleados" },
          { id: "worker-clock-settings", label: "Administrar código PIN" },
          { id: "worker-clock-card-settings", label: "Tarjeta de fichaje" },
        ],
        defaultExpanded: true,
        icon: <OrIcon name="employees" size={20} />,
        id: "personnel-management",
        label: "Gestión de Personal",
      },
      {
        children: [
          { id: "calendar", label: "Gestionar calendarios" },
          { id: "absences", label: "Gestionar ausencias" },
          { id: "vacations", label: "Asignar vacaciones" },
        ],
        defaultExpanded: true,
        icon: <OrIcon name="calendarCheck" size={20} />,
        id: "calendars-absences",
        label: "Calendarios y ausencias",
      },
      { icon: <OrIcon name="calendarClock" size={20} />, id: "shift", label: "Gestionar turnos" },
      { icon: <OrIcon name="workHistory" size={20} />, id: "worker-clock", label: "Gestionar fichajes" },
      { icon: <OrIcon name="folder" size={20} />, id: "documents", label: "Gestionar documentos" },
      {
        children: [
          { id: "headhunting", label: "Contratar servicios" },
          { id: "headhunting-candidates", label: "Gestión de candidatos" },
        ],
        defaultExpanded: true,
        icon: <OrIcon name="humanResources" size={20} />,
        id: "human-resources",
        label: "Recursos humanos",
      },
      {
        children: [{ id: "acl", label: "Permisos" }],
        defaultExpanded: true,
        icon: <OrIcon name="settings" size={20} />,
        id: "settings",
        label: "Configuración",
      },
    ],
    successEmail,
    summaryCard,
    supportCards: [
      createShortcutCard({
        description: "Accede al centro de ayuda y consulta la documentación operativa creada desde admin.",
        icon: <OrIcon name="sendMessage" size={20} />,
        title: "Centro de ayuda y soporte",
        variant: "shortcut",
      }),
    ],
  };
}
